/*
 * The arrows that walk a product card through the rest of its photographs.
 *
 * The card already carries two pictures: the one it rests on and the one the theme crossfades to under a
 * mouse. Those two are left exactly as the theme renders them, so the hover is untouched. Anything past the
 * second is fetched only when the shopper asks for it, by pointing the second picture at the next one in the
 * list. A page of twenty four cards therefore still loads two pictures a card rather than ten.
 *
 * While the shopper is walking a card the crossfade is held off, through a class the stylesheet reads;
 * without it, leaving and re-entering the card would drag the picture back to the second one. Taking a mouse
 * off the card puts it back to the beginning. A finger is not a mouse: see "handleLeave".
 *
 * The list is handed over on the arrows themselves as "data-media", the photographs after the first, separated
 * by spaces. Shopify's image_url never returns a space, so nothing has to be quoted or escaped to survive the
 * attribute. It sits there rather than on this element so that the card does not have to work the list out
 * twice, once to decide whether there are arrows worth drawing and once to hand it over.
 */
class HibCardMedia extends HTMLElement {
  connectedCallback() {
    this.nav = this.querySelector('[data-card-media-nav]');
    this.extras = ((this.nav && this.nav.dataset.media) || '').split(' ').filter(Boolean);
    this.secondary = this.querySelector('.product-card__image--secondary');

    if (this.extras.length === 0 || !this.secondary) return;

    // What the second picture looks like at rest, so it can be put back.
    this.restSrc = this.secondary.getAttribute('src');
    this.restSrcset = this.secondary.getAttribute('srcset');

    this.index = 0;
    this.previousButton = this.querySelector('[data-card-media="previous"]');
    this.nextButton = this.querySelector('[data-card-media="next"]');

    // This element is laid out as "display: contents" so that it does not disturb the figure, which leaves it
    // without a box of its own and so without pointer events. The figure is what the pointer actually leaves.
    this.figure = this.closest('.product-card__figure') || this;

    this.handleClick = this.handleClick.bind(this);
    this.handleLeave = this.handleLeave.bind(this);

    this.addEventListener('click', this.handleClick);
    this.figure.addEventListener('pointerleave', this.handleLeave);

    this.syncButtons();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    if (this.figure) this.figure.removeEventListener('pointerleave', this.handleLeave);
  }

  handleClick(event) {
    const button = event.target.closest('[data-card-media]');

    if (!button || !this.contains(button)) return;

    // The pictures sit inside a link to the product, which must not follow on an arrow.
    event.preventDefault();
    event.stopPropagation();

    this.goTo(this.index + (button.dataset.cardMedia === 'next' ? 1 : -1));
  }

  handleLeave(event) {
    // Only a mouse can leave. A touch screen destroys its pointer the moment the finger lifts, and the
    // browser reports that as leaving, in between the tap and the click the tap turns into: every tap was
    // being undone a few milliseconds before it was read, which is why the arrows did nothing on a phone.
    // The check is on the pointer rather than on the screen, so a laptop with a touch screen keeps both.
    if (event && event.pointerType && event.pointerType !== 'mouse') return;

    this.goTo(0);
  }

  goTo(index) {
    const clamped = Math.min(Math.max(index, 0), this.extras.length);

    if (clamped === this.index) return;
    this.index = clamped;

    if (this.index === 0) {
      // Back to rest, and the second picture goes back with it: the next hover has to show the photograph
      // the card is meant to crossfade to, not wherever the shopper happened to stop last time.
      this.restore();
      this.classList.remove('is-browsing');
    } else if (this.index === 1) {
      // The first step is the picture the theme already loaded, so it goes back with its own srcset.
      this.restore();
      this.classList.add('is-browsing');
    } else {
      // One picture, drawn at the width a card needs, so there is no set for the browser to choose from.
      this.secondary.removeAttribute('srcset');
      this.secondary.setAttribute('src', this.extras[this.index - 1]);
      this.classList.add('is-browsing');
    }

    this.syncButtons();
  }

  restore() {
    this.secondary.setAttribute('src', this.restSrc);

    if (this.restSrcset) {
      this.secondary.setAttribute('srcset', this.restSrcset);
    } else {
      this.secondary.removeAttribute('srcset');
    }
  }

  syncButtons() {
    if (this.previousButton) this.previousButton.disabled = this.index === 0;
    if (this.nextButton) this.nextButton.disabled = this.index === this.extras.length;
  }
}

if (!customElements.get('hib-card-media')) {
  customElements.define('hib-card-media', HibCardMedia);
}
